import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

interface GrecaptchaEnterprise {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
}
declare const grecaptcha: { enterprise: GrecaptchaEnterprise } | undefined;

const SCRIPT_LOAD_TIMEOUT_MS = 10000;

// grecaptcha.execute() has no built-in timeout - on a slow/blocked network it can hang
// indefinitely, which previously surfaced as an unhandled "reCAPTCHA Timeout" rejection with
// nothing to catch it. Racing it against our own timeout guarantees callers' try/catch always
// resolves one way or the other.
const EXECUTE_TIMEOUT_MS = 15000;

/**
 * Loads and calls the reCAPTCHA Enterprise script on demand. Only SIGNUP and CREATE_ORGANIZATION
 * use this - the script used to load unconditionally on every route via a blocking <script> tag
 * in index.html, running its badge/background verification on pages (e.g. the dashboard, project
 * create/edit) that never call execute() at all. Loading it here, only when execute() is first
 * called, keeps that badge and its network activity off every page that doesn't need it.
 */
@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private readonly siteKey = environment.recaptchaSiteKey;
  private scriptLoadPromise: Promise<void> | null = null;

  execute(action: string): Promise<string> {
    return this.loadScript().then(
      () =>
        new Promise<string>((resolve, reject) => {
          if (typeof grecaptcha === 'undefined' || !grecaptcha?.enterprise) {
            reject(new Error('reCAPTCHA failed to load. Please disable ad blockers and reload.'));
            return;
          }
          const { enterprise } = grecaptcha;
          const timer = setTimeout(() => reject(new Error('reCAPTCHA timed out. Please check your network and try again.')), EXECUTE_TIMEOUT_MS);
          enterprise.ready(async () => {
            try {
              const token = await enterprise.execute(this.siteKey, { action });
              clearTimeout(timer);
              resolve(token);
            } catch (err) {
              clearTimeout(timer);
              reject(err);
            }
          });
        }),
    );
  }

  private loadScript(): Promise<void> {
    if (typeof grecaptcha !== 'undefined' && grecaptcha?.enterprise) {
      return Promise.resolve();
    }
    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${this.siteKey}`;
      // Removing the element on failure (rather than leaving it in document.head) keeps a retry
      // (e.g. the user re-submitting after seeing the error) from accumulating orphaned script
      // tags for the life of the SPA session.
      const fail = () => {
        script.remove();
        this.scriptLoadPromise = null;
        reject(new Error('reCAPTCHA failed to load. Please disable ad blockers and reload.'));
      };
      const timer = setTimeout(fail, SCRIPT_LOAD_TIMEOUT_MS);
      script.onload = () => {
        clearTimeout(timer);
        // Some ad blockers serve a stubbed 200-OK empty response instead of blocking the
        // request outright - it still fires onload, but leaves grecaptcha unset.
        if (typeof grecaptcha === 'undefined' || !grecaptcha?.enterprise) {
          fail();
          return;
        }
        resolve();
      };
      script.onerror = () => {
        clearTimeout(timer);
        fail();
      };
      document.head.appendChild(script);
    });

    return this.scriptLoadPromise;
  }
}
